// src/components/custom-ui/DeleteConfirmDialog.tsx
// Type-the-name confirmation pattern for destructive operations.
// The destructive button stays disabled until the user types `entityName` exactly.
import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle } from 'lucide-react';

interface DeleteConfirmDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Dialog title, e.g. "Radera Organisation" */
    title: string;
    /** Display label of the entity in body copy, e.g. "organisationen" */
    entityLabel: string;
    /** The exact string the user must retype to confirm. */
    entityName: string;
    /** Optional cascade-warning sentence shown below the entity callout. */
    cascadeWarning?: string;
    /** Called when the user confirms; parent handles the actual API call. */
    onConfirm: () => void | Promise<void>;
    /** Disables the confirm button while parent's request is in flight. */
    isDeleting?: boolean;
    /** Override the confirm button label (defaults to "Radera"). */
    confirmLabel?: string;
}

export const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
    open,
    onOpenChange,
    title,
    entityLabel,
    entityName,
    cascadeWarning,
    onConfirm,
    isDeleting = false,
    confirmLabel = 'Radera',
}) => {
    const [typed, setTyped] = useState('');

    // Reset the typed value whenever the dialog closes (or the target changes).
    useEffect(() => {
        if (!open) setTyped('');
    }, [open, entityName]);

    const matches = typed.trim() === entityName.trim();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-600">
                        <AlertTriangle className="h-5 w-5" />
                        {title}
                    </DialogTitle>
                    <DialogDescription className="space-y-2">
                        <span className="block">
                            Är du säker på att du vill radera {entityLabel}{' '}
                            <strong>{entityName}</strong>?
                        </span>
                        {cascadeWarning && (
                            <span className="block text-red-700">
                                {cascadeWarning}
                            </span>
                        )}
                        <span className="block pt-2">
                            Detta går inte att ångra.
                        </span>
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2 py-2">
                    <Label htmlFor="delete-confirm-input" className="text-sm">
                        Skriv <code className="px-1 py-0.5 bg-gray-100 rounded font-mono text-xs">{entityName}</code> för att bekräfta:
                    </Label>
                    <Input
                        id="delete-confirm-input"
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        placeholder={entityName}
                        autoFocus
                        autoComplete="off"
                        // Visual cue: red border once the user starts typing but hasn't matched yet
                        className={
                            typed.length > 0 && !matches
                                ? 'border-red-300 focus-visible:ring-red-400'
                                : matches
                                    ? 'border-green-400 focus-visible:ring-green-400'
                                    : ''
                        }
                    />
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isDeleting}
                    >
                        Avbryt
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={onConfirm}
                        disabled={!matches || isDeleting}
                    >
                        {isDeleting ? 'Raderar...' : confirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default DeleteConfirmDialog;
